/**
 * 샵관리 — 예약 저장소 (localStorage, BE 연결 전)
 *
 * 예약 행 형태 (네이버예약 등 외부 데이터도 이 모양으로 정규화해서 넣는다):
 *  { reservationUid, source: 'MANUAL'|'NAVER', externalId, startDt: 'yyyy-MM-dd HH:mm',
 *    endDt, customerNm, phone, serviceNm, staffNm, status, memo, raw }
 *  - source + externalId 가 외부 예약의 식별키. 같은 키가 다시 오면 갱신(upsert)한다.
 *  - raw 는 외부 원본 payload 보관용(디버깅·재매핑).
 *
 * 네이버예약 연동 자리: 아래 upsertExternalReservations(tc, 'NAVER', items) 만 호출하면 된다.
 * (원본 → 이 형태로 바꾸는 매퍼는 API 스펙 확정 후 작성)
 */
import { loadTable, saveTable, createUid } from 'shop/shopStore';

export const RES_STATUS_OPTIONS = [
  { value: 'CONFIRMED', label: '예약확정' },
  { value: 'DONE', label: '방문완료' },
  { value: 'NOSHOW', label: '노쇼' },
  { value: 'CANCELED', label: '취소' },
];
export const statusLabel = (v) => RES_STATUS_OPTIONS.find((o) => o.value === v)?.label || v || '';

export const SOURCE_LABEL = { MANUAL: '직접', NAVER: '네이버' };

const TABLE = 'reservation';

/** 기간 조회 (from/to 는 'yyyy-MM-dd', 양끝 포함) */
export function loadReservations(tc, from, to) {
  return loadTable(tc, TABLE)
    .filter((r) => {
      const d = String(r.startDt || '').slice(0, 10);
      return (!from || d >= from) && (!to || d <= to);
    })
    .sort((a, b) => String(a.startDt).localeCompare(String(b.startDt)));
}

export function addReservation(tc, data) {
  const row = {
    reservationUid: createUid('RSV'),
    source: 'MANUAL',
    externalId: '',
    endDt: '',
    status: 'CONFIRMED',
    memo: '',
    ...data,
    createdAt: new Date().toISOString(),
  };
  saveTable(tc, TABLE, [...loadTable(tc, TABLE), row]);
  return row;
}

export function updateReservation(tc, reservationUid, patch) {
  saveTable(
    tc,
    TABLE,
    loadTable(tc, TABLE).map((r) => (r.reservationUid === reservationUid ? { ...r, ...patch } : r))
  );
}

export function removeReservation(tc, reservationUid) {
  saveTable(tc, TABLE, loadTable(tc, TABLE).filter((r) => r.reservationUid !== reservationUid));
}

/**
 * 외부(네이버예약 등) 예약 반영 — source+externalId 가 같으면 갱신, 없으면 추가.
 * 갱신 시 원장이 화면에서 바꾼 memo·staffNm 은 외부 값이 비어 있으면 유지한다.
 * @returns {{added:number, updated:number}}
 */
export function upsertExternalReservations(tc, source, items) {
  const rows = loadTable(tc, TABLE);
  let added = 0;
  let updated = 0;
  for (const it of items || []) {
    if (!it?.externalId || !it?.startDt) continue;
    const idx = rows.findIndex((r) => r.source === source && r.externalId === String(it.externalId));
    if (idx >= 0) {
      const prev = rows[idx];
      rows[idx] = {
        ...prev,
        ...it,
        source,
        externalId: String(it.externalId),
        reservationUid: prev.reservationUid,
        memo: it.memo || prev.memo,
        staffNm: it.staffNm || prev.staffNm,
      };
      updated += 1;
    } else {
      rows.push({
        reservationUid: createUid('RSV'),
        endDt: '',
        status: 'CONFIRMED',
        memo: '',
        ...it,
        source,
        externalId: String(it.externalId),
        createdAt: new Date().toISOString(),
      });
      added += 1;
    }
  }
  saveTable(tc, TABLE, rows);
  return { added, updated };
}
